import { Core } from "@faire/design-tokens";
import { AccessibleOutlineCSS } from "@faire/retailer-visitor-shared/components/AccessibleOutlineCSS";
import { PerStyleVariationMapBuilder } from "@faire/retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { Subtract } from "@faire/web/slate/icons/foundation/Subtract";
import { MeasurementInput } from "@faire/web/slate/Input/MeasurementInput";
import { Row } from "@faire/web/slate/Layout";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

export const OpenSizingVariationInput: React.FC<{
  variation: PerStyleVariationMapBuilder.IPerStyleOptionVariation;
  onChange: (value: number) => void;
}> = ({ variation, onChange }) => {
  const label = variation.size;
  const value = variation.selectedQuantity;
  const disabled = variation.inventory === 0;
  const quantityThreshold = variation.inventory;
  const inputError = quantityThreshold !== 0 && value > quantityThreshold;

  const handleSubtractValue = () => onChange(value - 1);
  const handleAddValue = () => onChange(value + 1);
  const handleCustomValue = (value?: number) => onChange(value ?? 0);

  return (
    <Row align="center" gap="1x">
      <IconButton onClick={handleSubtractValue} disabled={disabled}>
        <Subtract color="inherit" />
      </IconButton>
      <Tooltip
        isOpen
        hidden={!inputError}
        width={120}
        targetNode={
          <StyledNumberInput
            precision={0}
            value={disabled ? 0 : value}
            onChange={handleCustomValue}
            disabled={disabled}
            error={inputError}
            data-test-id={`open-sizing-size-quantity-${label}`}
          />
        }
      >
        <Typography color="inherit">
          <StrictLocalMsg
            defaultMessage="{quantityThreshold, plural, one{Only # piece left} other{Only # pieces left}}"
            description={{
              text: "total number of pieces left for the product",
            }}
            values={DEPRECATED_backwardsCompatibleValues({ quantityThreshold })}
          />
        </Typography>
      </Tooltip>
      <IconButton onClick={handleAddValue} disabled={disabled}>
        <Add />
      </IconButton>
    </Row>
  );
};

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  ${AccessibleOutlineCSS}

  &:hover {
    background: ${Core.surface.subdued};
  }

  &:disabled {
    background-color: ${Core.surface.inverse};
    color: ${Core.border.muted};
    cursor: default;
  }
`;

const StyledNumberInput = styled(MeasurementInput)`
  width: 44px;
  height: 30px;
  padding: 0;

  input {
    text-align: center;
  }
`;
